'use client'

import { IconButton, type IconButtonProps } from '@chakra-ui/react'
import { forwardRef } from 'react'
import { FiX } from 'react-icons/fi'

export interface CloseButtonProps
  extends Omit<IconButtonProps, 'appearance' | 'aria-label' | 'children'> {
  /** Accessible label announced for the close control. */
  label: string
  /** Optional close callback for consumers outside Chakra Dialog. */
  onClose?: () => void | Promise<void>
  /** Selects the control treatment for overlays or light surfaces. */
  appearance?: 'overlay' | 'surface'
}

/** The standard Assiduous close control shared by dialogs and full views. */
export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(
  function CloseButton(
    { label, onClose, onClick, appearance = 'surface', ...props },
    ref
  ) {
    const isSurface = appearance === 'surface'

    return (
      <IconButton
        ref={ref}
        type="button"
        aria-label={label}
        variant="ghost"
        size="xs"
        position="absolute"
        top={{ base: 2, md: 3 }}
        right={{ base: 2, md: 3 }}
        zIndex={20}
        minW="34px"
        w="34px"
        h="34px"
        p="8px"
        borderRadius="6px"
        borderWidth="0"
        bg="transparent"
        color={isSurface ? '#000000' : 'whiteAlpha.900'}
        _hover={{ bg: 'transparent' }}
        _active={{ bg: 'transparent' }}
        _focusVisible={{
          boxShadow: '0 0 0 2px var(--chakra-colors-blue-200)',
        }}
        {...props}
        onClick={(event) => {
          onClick?.(event)

          if (!event.defaultPrevented) {
            void onClose?.()
          }
        }}
      >
        <FiX size={18} strokeWidth={1.5} />
      </IconButton>
    )
  }
)
